import React, { memo, useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Modal } from '@Sage/hoi-poi-ui';
import SignatureCanvas from 'react-signature-canvas';

import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormDetails');

function Signature({ isOpen, onConfirm, onCancel, overlayClassName, ...props }) {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [signature, setSignature] = useState();
    const [disableConfirm, setDisableConfirm] = useState(false);

    const sigCanvasRef = useRef(null);

    const onEndDraw = useCallback(() => {
        if (!sigCanvasRef.current.isEmpty()) {
            setDisableConfirm(false);
            setSignature(sigCanvasRef.current.toDataURL('image/jpeg'));
        }
    }, []);

    const onClear = useCallback(() => {
        sigCanvasRef.current.clear();
        setSignature('');
        setDisableConfirm(true);
    }, []);

    const handleOnConfirm = useCallback(() => {
        onConfirm(signature.replace('data:image/jpeg;base64,', ''));
        sigCanvasRef.current.clear();
        setSignature();
    }, [onConfirm, signature]);

    const signatureRef = useCallback(
        (ref) => {
            if (ref) {
                const canvas = ref.getCanvas();
                const context = canvas.getContext('2d');
                context.fillStyle = 'white';
                context.fillRect(0, 0, canvas.width, canvas.height);
                signature && ref.fromDataURL(signature, { width: 458, height: 198 });
            }

            sigCanvasRef.current = ref;
            if (ref?.isEmpty()) {
                setDisableConfirm(true);
            }
        },
        [signature],
    );

    const canvasProps = useMemo(
        () => ({ width: 458, height: 198, className: classes.signatureCanvas }),
        [classes.signatureCanvas],
    );

    return (
        <Modal
            className={classes.root}
            overlayClassName={overlayClassName}
            title={t('Signature')}
            isOpen={isOpen}
            onRequestClose={onCancel}
            onCancel={onClear}
            onConfirm={handleOnConfirm}
            cancelText={t('Clear')}
            confirmText={t('Acept')}
            overrides={{ confirmButton: { isDisabled: disableConfirm } }}
        >
            <SignatureCanvas
                penColor="black"
                canvasProps={canvasProps}
                ref={signatureRef}
                onEnd={onEndDraw}
            />
        </Modal>
    );
}

Signature.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default memo(Signature);
